<template>
  <card-container
    :id="pageId"
    :class="pageId"
    :is-loading="status.isLoading"
    loading-message="Loading Ride Detail..."
    show-close
    no-action
    @close="initRideId"
  >
    <template #title>
      Ride Detail Info
    </template>

    <template #content>
      <div
        v-if="!!rideInfo"
        class="custom-collapse"
      >
        <div class="flex-container">
          <div class="left-container">
            <table>
              <tr
                v-for="(item, index) in leftTableColumns"
                :key="index"
              >
                <td>
                  <b>{{ item.label }}:</b>
                </td>
                <td>
                  <read-more
                    v-if="item.isReadMore"
                    more-str="more..."
                    class="read-more"
                    :more-txt-class="item.moreTxtClass"
                    :class="item.customClass || ''"
                    :text="rideInfo[item.field]"
                    :max-chars="item.maxLength || 40"
                  />

                  <div v-else-if="item.isList">
                    <div
                      v-if="rideInfo[item.field].comment"
                      class="list-comment"
                    >
                      {{ rideInfo[item.field].comment }}
                    </div>
                    <ol :class="item.customClass || ''">
                      <li
                        v-for="(change, ind) in rideInfo[item.field].list"
                        :key="ind"
                      >
                        <div v-dompurify-html="change ?? ''" />
                      </li>
                    </ol>
                  </div>

                  <div
                    v-else
                    :class="item.customClass"
                  >
                    <span v-dompurify-html="formatText(item, rideInfo[item.field]) ?? ''" />
                  </div>
                </td>
              </tr>
            </table>
          </div>

          <div class="right-container">
            <table>
              <tr
                v-for="(item, index) in rightTableColumns"
                :key="index"
              >
                <td>
                  <b>{{ item.label }}:</b>
                </td>
                <td>
                  <read-more
                    v-if="item.isReadMore"
                    more-str="more..."
                    class="read-more"
                    :more-txt-class="item.moreTxtClass"
                    :class="item.customClass || ''"
                    :text="rideInfo[item.field]"
                    :max-chars="item.maxLength || 40"
                  />

                  <div v-else-if="item.isList">
                    <div
                      v-if="rideInfo[item.field].comment"
                      class="list-comment"
                    >
                      {{ rideInfo[item.field].comment }}
                    </div>
                    <ol :class="item.customClass || ''">
                      <li
                        v-for="(change, ind) in rideInfo[item.field].list"
                        :key="ind"
                      >
                        <div v-dompurify-html="change ?? ''" />
                      </li>
                    </ol>
                  </div>

                  <div
                    v-else
                    :class="item.customClass"
                  >
                    <span v-dompurify-html="formatText(item, rideInfo[item.field]) ?? ''" />
                  </div>
                </td>
              </tr>
            </table>
          </div>
        </div>
      </div>
      <div
        v-if="status.isLoading"
        class="no-data-viewer"
      />
    </template>

    <message-html :result-html="status.errorMessage" />
  </card-container>
</template>

<script setup lang="ts">
import { ref, Ref, reactive, watch, computed } from 'vue';

import util from '@/Utils/util';
import rideUtility from '@/Utils/rideUtility';
import { useDashboard } from '@/use/app';
import { useRide } from '@/use/ride';
import { sendGet } from '@/Utils/api';
import { TableColumnType } from '@/models/table';
import { commonDateTimeFormat } from '@/constants/app';

const pageId = 'ride-detail-info';

const operatorTableLeftColumns: TableColumnType[] = [
  { label: 'Customer', field: 'customerName' },
  { label: 'Ride ID', field: 'id' },
  { label: 'Request ID', field: 'requestId' },
  { label: 'Date and Time', field: 'createdAt', isDateField: true },
  { label: 'Scheduled Ride ID', field: 'scheduledRideId' },
  { label: 'Status', field: 'status' },
  { label: 'Paid Status', field: 'paidStatus' },
  { label: 'Ride Purpose', field: 'partnerRidePurpose' },
  { label: 'Service Type', field: 'serviceType' },
  { label: 'Delivery Details', field: 'deliveryDetails', isList: true, customClass: 'delivery-details-list' },
  { label: 'Caller ID', field: 'callerId' },
  { label: 'Custom Rider Name', field: 'customRiderName' },
  { label: 'Ride Phone Number', field: 'riderPhone' },
  { label: 'Ride Vendor', field: 'vendor' },
  { label: 'Corp Account Id', field: 'dummyId' },
  { label: 'Ride Type', field: 'rideType' },
  { label: 'Ordered By', field: 'orderedBy' },
  { label: 'Start Location', field: 'startLocation' },
  { label: 'Start Lat', field: 'startLat' },
  { label: 'Start Long', field: 'startLong' },
  { label: 'Start Name', field: 'startName' },
  { label: 'Cur Lat', field: 'curLat' },
  { label: 'Cur Lng', field: 'curLng' },
  { label: 'Last Lat', field: 'lastLat' },
  { label: 'Last Lng', field: 'lastLng' },
  { label: 'End Location', field: 'endLocation' },
  { label: 'End Lat', field: 'endLat' },
  { label: 'End Long', field: 'endLong' },
  { label: 'Destination', field: 'destination' },
  { label: 'Dest Lat', field: 'destinationLat' },
  { label: 'Dest Lng', field: 'destinationLng' },
  { label: 'Dest Name', field: 'destinationName' },
  { label: 'Intermediate stops', field: 'intermediateStopsDetail', isList: true, customClass: 'intermediate-stops-list' },
  { label: 'End Note', field: 'endNote' },
  { label: 'Need Love ID', field: 'needLoveId' },
  { label: 'Duration', field: 'duration' },
  { label: 'Distance', field: 'distance' },
  { label: 'Map', field: 'map', width: 240, isReadMore: true, maxLength: 60 },
  { label: 'Car ID', field: 'carId' },
  { label: 'Driver Name', field: 'driverName' },
  { label: 'Driver Phone', field: 'driverPhone' },
  { label: 'Driver Photo', field: 'driverPhoto', isReadMore: true, maxLength: 60 },
  { label: 'Driver Car Image', field: 'driverCarImage', isReadMore: true, maxLength: 60 },
  { label: 'Driver Changes', field: 'driverChanges' },
  { label: 'Driver Plate', field: 'driverPlate' },
  { label: 'Driver Rating', field: 'driverRating' },
  { label: 'Driver Cancels', field: 'driverCancels' },
  { label: 'Driver Taking a Long Time', field: 'driverTakingLongTime' },
  { label: 'Driver Wait More Than 7Mins', field: 'driverWaitMoreThan7Mins' },
  { label: 'Driver Joy Ride', field: 'driverJoyRide' },
  { label: 'Driver Calls', field: 'driverCalls' },
  { label: 'Driver Texts', field: 'driverTexts' },
  { label: 'Call Rider Driver Arriving', field: 'callRiderDriverArriving' },
  { label: 'Note To Driver', field: 'noteToDriverHtml', isReadMore: true, maxLength: 60 },
  { label: 'Time Accepted', field: 'timeAccepted', isDateField: true },
  { label: 'Time Arriving', field: 'timeArriving', isDateField: true },
  { label: 'Time In Progress', field: 'timeInProgress', isDateField: true },
  { label: 'Time Completed', field: 'timeCompleted', isDateField: true },
  { label: 'Time Receipt Ready', field: 'timeReceiptReady', isDateField: true },
  { label: 'Time Canceled', field: 'timeCanceled', isDateField: true },
  { label: 'Time Zone', field: 'timeZone' },
];
const operatorTableRightColumns: TableColumnType[] = [
  { label: 'ETA', field: 'eta' },
  { label: 'Accepted ETA', field: 'acceptedEta' },
  { label: 'Dummy ID', field: 'dummyId' },
  { label: 'Digit Pressed', field: 'digitPressed' },
  { label: 'Switched Vendors', field: 'switchedVendors' },
  { label: 'Skipped Status', field: 'skippedStatus' },
  { label: 'Wrong Status', field: 'wrongStatus' },
  { label: 'Receipt Call', field: 'receiptCall' },
  { label: 'Cost Per Min', field: 'costPerMin' },
  { label: 'Cost Per Mile', field: 'costPerMile' },
  { label: 'Base Fare', field: 'baseFare' },
  { label: 'Distance Fee', field: 'distanceFee' },
  { label: 'Time Fee', field: 'timeFee' },
  { label: 'Normal Fare', field: 'normalFare' },
  { label: 'Booking Fee', field: 'bookingFee' },
  { label: 'Minimum Fee', field: 'minimumFee' },
  { label: 'Tip', field: 'tip' },
  { label: 'Surge Url', field: 'surgeUrl', isReadMore: true, maxLength: 40 },
  { label: 'Surge Id', field: 'surgeId', isReadMore: true, maxLength: 60 },
  { label: 'Subtotal', field: 'subtotal' },
  { label: 'Vendor Total Fare', field: 'totalFare' },
  { label: 'Concierge Fee', field: 'gogoRevenue' },
  { label: 'GoGo Fee', field: 'gogoFee' },
  { label: 'GoGo Mins', field: 'gogoMins' },
  { label: 'Total Fare', field: 'gogoCost' },
  { label: 'Customer Cost Override', field: 'customerCostOverride' },
  { label: 'Customer Cost', field: 'customerCost' },
  { label: 'Customer Charge ID', field: 'customerChargeId' },
  { label: 'Time Customer Charged', field: 'timeCustomerCharged', isDateField: true },
  { label: 'Customer Refund', field: 'customerRefund' },
  { label: 'Customer Refund ID', field: 'customerRefundId' },
  { label: 'Customer Charge Decline', field: 'customerChargeDecline', isList: true },
  { label: 'Time Customer Refunded', field: 'timeCustomerRefunded', isDateField: true },
  { label: 'Customer Credits', field: 'customerCredits' },
  { label: 'Customer Credits Refund', field: 'customerCreditsRefund' },
  { label: 'Time Customer Credits Refunded', field: 'timeCustomerCreditsRefunded', isDateField: true },
  { label: 'Partner ID', field: 'partnerId' },
  { label: 'Partner Cost', field: 'partnerCost' },
  { label: 'Partner Charge ID', field: 'partnerChargeId' },
  { label: 'Partner Charge Decline', field: 'partnerChargeDecline', isList: true },
  { label: 'Time Partner Charged', field: 'timePartnerCharged', isDateField: true },
  { label: 'Partner Refund', field: 'partnerRefund' },
  { label: 'Partner Refund Id', field: 'partnerRefundId' },
  { label: 'Time Partner Refunded', field: 'timePartnerRefunded', isDateField: true },
  { label: 'Partner Credits', field: 'partnerCredits' },
  { label: 'Partner Credits Refund', field: 'partnerCreditsRefund' },
  { label: 'Time Partner Credits Refunded', field: 'timePartnerCreditsRefunded', isDateField: true },
  { label: 'Is Emergency', field: 'isEmergency' },
  { label: 'Emergency Reason', field: 'emergencyReason' },
  { label: 'Time Customer Credits Used', field: 'timeCustomerCreditsUsed', isDateField: true },
  { label: 'Time Partner Credits Used', field: 'timePartnerCreditsUsed', isDateField: true },
  { label: 'Reason', field: 'reason' },
  { label: 'Reason for Canceling', field: 'reasonForCanceling', isReadMore: true, maxLength: 60 },
  { label: 'Redispatches', field: 'redispatches' },
  { label: 'Expand Driver Search', field: 'isExpandDriverSearch', isCapitalize: true },
  { label: 'Expand Vehicle Search', field: 'isExpandVehicleSearch', isCapitalize: true },
  { label: 'Personal Ride', field: 'isDoNotApplySubsidy', isCapitalize: true },
  { label: 'Not Apply Expiring Credits', field: 'isDoNotApplyExpiringCredits', isCapitalize: true },
  { label: 'Previous Drivers', field: 'previousDrivers', isReadMore: true, maxLength: 90, moreTxtClass: 'pl-20 mobile-pl-20' },
];
const partnerTableLeftColumns: TableColumnType[] = [
  { label: 'Created At', field: 'createdAt' },
  { label: 'Time In Progress', field: 'timeInProgress' },
  { label: 'Time Completed', field: 'timeCompleted' },
  { label: 'Customer', field: 'customerName' },
  { label: 'Date Of Birth', field: 'dob' },
  { label: 'Phone Number', field: 'riderPhone' },
  { label: 'Request ID', field: 'requestId' },
  { label: 'Custom Rider Name', field: 'customRiderName' },
  { label: 'Distance', field: 'distance' },
  { label: 'Duration', field: 'duration' },
  { label: 'Ride Purpose', field: 'partnerRidePurpose' },
  { label: 'Service Type', field: 'serviceType' },
  { label: 'Start Location', field: 'startLocation' },
  { label: 'End Location', field: 'endLocation' },
  { label: 'Destination', field: 'destination' },
  { label: 'Intermediate stops', field: 'intermediateStopsDetail', isList: true, customClass: 'intermediate-stops-list' },
  { label: 'Ride Vendor', field: 'vendor' },
  { label: 'Ride Type', field: 'rideType' },
  { label: 'Driver Name', field: 'driverName' },
  { label: 'Total Fare', field: 'gogoCost' },
  { label: 'Customer Cost', field: 'customerCost' },
  { label: 'Customer Charge ID', field: 'customerChargeId' },
  { label: 'Time Customer Charged', field: 'timeCustomerCharged', isDateField: true },
  { label: 'Customer Refund', field: 'customerRefund' },
  { label: 'Customer Refund ID', field: 'customerRefundId' },
];
const partnerTableRightColumns: TableColumnType[] = [
  { label: 'Customer Charge Decline', field: 'customerChargeDecline', isList: true },
  { label: 'Time Customer Refunded', field: 'timeCustomerRefunded', isDateField: true },
  { label: 'Customer Credits', field: 'customerCredits' },
  { label: 'Customer Credits Refund', field: 'customerCreditsRefund' },
  { label: 'Time Customer Credits Refunded', field: 'timeCustomerCreditsRefunded', isDateField: true },
  { label: 'Partner ID', field: 'partnerId' },
  { label: 'Partner Cost', field: 'partnerCost' },
  { label: 'Partner Charge ID', field: 'partnerChargeId' },
  { label: 'Partner Charge Decline', field: 'partnerChargeDecline', isList: true },
  { label: 'Time Partner Charged', field: 'timePartnerCharged', isDateField: true },
  { label: 'Partner Refund', field: 'partnerRefund' },
  { label: 'Partner Refund Id', field: 'partnerRefundId' },
  { label: 'Time Partner Refunded', field: 'timePartnerRefunded', isDateField: true },
  { label: 'Partner Credits', field: 'partnerCredits' },
  { label: 'Partner Credits Refund', field: 'partnerCreditsRefund' },
  { label: 'Time Partner Credits Refunded', field: 'timePartnerCreditsRefunded', isDateField: true },
  { label: 'Partner Rides', field: 'partnerRides' },
  { label: 'Time Zone', field: 'timeZone' },
  { label: 'Surge Url', field: 'surgeUrl', isReadMore: true, maxLength: 40 },
  { label: 'Surge Id', field: 'surgeId', isReadMore: true, maxLength: 60 },
  { label: 'Note To Driver', field: 'noteToDriverHtml', isReadMore: true, maxLength: 60 },
  { label: 'Reason for Ride', field: 'reason' },
];

const { isPartnerDashboard } = useDashboard();
const { rideId, setRideId } = useRide();

const rideInfo: Ref<any> = ref(null);

const status = reactive({
  isLoading: false,
  errorMessage: '',
});

const leftTableColumns = computed(() => (isPartnerDashboard.value ? partnerTableLeftColumns : operatorTableLeftColumns));

const rightTableColumns = computed(() => (isPartnerDashboard.value ? partnerTableRightColumns : operatorTableRightColumns));

const getRideDetailInfo = async () => {
  status.errorMessage = '';
  rideInfo.value = null;

  status.isLoading = true;
  try {
    const response = await sendGet(`rides/${rideId.value}`);
    rideInfo.value = rideUtility.formatRideData(response.data);
  } catch (error) {
    status.errorMessage = util.getErrorResponse(error);
  }
  status.isLoading = false;
};

const formatText = (column: TableColumnType, value: string) => {
  if (column.isDateField) {
    return util.formatDateTime(value, commonDateTimeFormat);
  }

  return value;
};

const initRideId = () => {
  setRideId(0);
};

watch(
  rideId,
  (value: number | null) => {
    if (value) {
      getRideDetailInfo();
    }
  },
  {
    immediate: true,
  },
);
</script>

<style lang="scss">
.page-content.ride-detail-info {
  .no-data-viewer {
    height: 70px;
  }

  .custom-collapse {
    .left-container,
    .right-container {
      p {
        word-break: break-all;
      }
    }

    table {
      td {
        font-size: 14px;
        &:nth-child(odd) {
          min-width: 180px;
          width: 180px;
          color: #5e8283;
        }

        a#readmore {
          padding-left: 0 !important;
          color: #f00;
        }

        ol {
          padding-left: 10px;
          margin-bottom: 5px;

          li div {
            word-break: break-word;
          }
        }

        ol.intermediate-stops-list {
          list-style: decimal;
          padding-left: 1.25rem;

          li + li {
            margin-top: 0.5rem;
          }
        }

        ol.delivery-details-list {
          list-style: none;
          padding-left: 0;

          li + li {
            margin-top: 0.25rem;
          }
        }
      }
    }
  }
}
</style>
